import { sendChatMessage, setChatInputText } from 'features/chat/chatSlice';
import React, { FunctionComponent, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'rootReducer';
import styles from './styles.module.scss';
import { useCurrentUser } from 'features/current-user/hooks';
import EmojiPicker from 'components/EmojiPicker';
import { EmojiClickData } from 'emoji-picker-react';
import GiphyPicker from 'components/GiphyPicker';
import type { GifImage } from 'gif-picker-react-klipy';
import ContentEditable, { ContentEditableEvent } from 'react-contenteditable';
import { HeartButton } from 'components/Hearts/HeartButton';
import { useAppSelector } from 'hooks/redux';

interface ChatInputProps {
  channelId: string | null;
  initialContent?: string;
  onSubmit?: (text: string) => void;
  onCancel?: () => void;
  submitLabel?: string;
}

export const ChatInput: FunctionComponent<ChatInputProps> = (props) => {
  const textareaRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const socketId = useSelector((state: RootState) => state.hub.socketId);
  const reduxChatInputText = useAppSelector(
    (state) => state.chat.chatInputText
  );
  const [localText, setLocalText] = useState(props.initialContent ?? '');
  const currentUser = useCurrentUser();
  const chatLabel = currentUser
    ? `Chat as ${currentUser.username}...`
    : 'Chat now...';

  const isEditMode = !!props.onSubmit;
  const inputText = isEditMode ? localText : reduxChatInputText;

  const setInputText = (text: string) => {
    if (isEditMode) {
      setLocalText(text);
    } else {
      dispatch(setChatInputText(text));
    }
  };

  const submitForm = () => {
    const formatted = inputText
      .replace(/<div>/gi, '<br>')
      .replace(/<\/div>/gi, '')
      .replace(/^<br>/, '');

    if (isEditMode) {
      if (!formatted.trim()) return;
      props.onSubmit?.(formatted);
    } else {
      if (!props.channelId) return;
      dispatch(sendChatMessage(props.channelId, formatted, socketId));
      dispatch(setChatInputText(''));
    }

    if (textareaRef.current) {
      textareaRef.current.blur();
      textareaRef.current.focus();
    }
  };

  const sendMessage = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    submitForm();
  };

  const onKeyPress = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitForm();
    }
  };

  const handleOnChange = (e: ContentEditableEvent) => {
    setInputText(e.target.value);
  };

  const onEmojiClick = (emojiObject: EmojiClickData) => {
    setInputText(inputText + emojiObject.emoji);
  };

  const onGifClick = (gif: GifImage) => {
    const text = filterTextImages(gif.url, gif.description);
    setInputText(inputText + text);
    textareaRef.current?.focus();
  };

  const filterTextImages = (url: string, description: string) => {
    const filterStringImages = url.replace(
      /(https?:\/\/\S+(\.gif))/g,
      `<img src="$&" alt="${description}"/>`
    );
    return filterStringImages;
  };

  return (
    <div className={styles['chat__input']}>
      <form
        onSubmit={sendMessage}
        className={styles['chat__form']}
        onKeyPress={onKeyPress}
      >
        <div className="tw-flex-1 tw-flex tw-space-x-2 tw-items-center">
          {!isEditMode && <HeartButton />}
          <div className="tw-flex-1">
            <ContentEditable
              innerRef={textareaRef}
              html={inputText}
              disabled={false}
              onChange={handleOnChange}
              className={styles['chat__textarea']}
              placeholder={chatLabel}
            />
          </div>
        </div>
        <div className="tw-flex-none tw-flex tw-space-x-2 tw-items-center">
          <GiphyPicker onGifClick={onGifClick} />
          <EmojiPicker onEmojiClick={onEmojiClick} />
          <button className={styles['chat__submit']} type="submit">
            {props.submitLabel ?? 'Post'}
          </button>
          {props.onCancel && (
            <button
              className={styles['chat__submit']}
              type="button"
              onClick={props.onCancel}
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};
